import type { Dispatch } from "react";
import {
  BILLING,
  COST_CENTER,
  GENERAL,
  INTERNAL_INFO,
  REMUNERATION,
  TIME_TABLE,
  UPDATE_FIELD_ERROR,
  UPDATE_TAB_ERROR,
} from "../../annotation/PlanningConstants";
import { validateAdv, validateRequired, validateSelectField } from "components/common/services/ValidationService";
import type { TimeStates } from "components/common/utlis/PlanningUtility";
import { t } from "components/CentralDataMangement/translation/Translation";
import type { FieldValue } from "components/common/utlis/TypeAnnotations";

// Define types for props if necessary
export interface MandatoryFields {
  [key: string]: string[];
}

export const validateMandatoryFields = (
  state: any,
  dispatch: Dispatch<any>,
  type: string
): boolean => {
  const activeTab = state.tabDetails.find((tab: any) => tab.showStatus);
  const activeTabId = activeTab?.id;
  const baseTimeTableFields = [
    "advStatus"
  ];

  const mandatoryFieldMapping: { [key: string]: string[] } =
    type === "draft"
      ? {
      }
      : {
        timeTable:
          state.timeTableDetails?.regime?.value === 2 ? ["shift", ...baseTimeTableFields] : baseTimeTableFields,
        remuneration: ['grossPerHr'],
      };

  const errorsByTab: { [tabId: string]: { [fieldName: string]: string } } = {};

  state.tabDetails
    .slice(
      0,
      state.tabDetails.findIndex((tab: any) => tab.id === activeTabId) + 1
    )
    .forEach((tab: any) => {
      const tabId = tab.id;
      let tabFields = {};

      switch (tabId) {
        case BILLING:
          tabFields = state.billingDetails;
          break;
        case REMUNERATION:
          tabFields = state.remenurationDetails;
          break;
        case TIME_TABLE:
          tabFields = state.timeTableDetails;
          break;
        default:
          break;
      }

      const validationRules: {
        [fieldName: string]: ((value: any) => string | null | undefined)[];
      } = {
        shift: [validateSelectField],
        grossPerHr: [validateRequired],
        advStatus: [validateAdv],
      };

      const mandatoryFields = mandatoryFieldMapping[tabId] || [];

      const errorsInTab: { [fieldName: string]: string } = {};
      for (const fieldName of mandatoryFields) {
        const fieldValidationRules = validationRules[fieldName];

        let fieldValue: FieldValue = tabFields[fieldName as keyof typeof tabFields];
        if (fieldName === "advStatus") {
          fieldValue = {
            paidAdv: state.timeTableDetails.paidAdv,
            unpaidAdv: state.timeTableDetails.unPaidAdv,
            effectiveHrs: state.timeTableDetails.effectiveHrsFullTime,
            avgHrs: state.timeTableDetails.avgHrsFullTime,
          };
        }

        if (fieldValidationRules) {
          for (const rule of fieldValidationRules) {
            const validationError = rule(fieldValue);
            if (validationError) {
              errorsInTab[fieldName] = validationError;
              dispatch({
                type: UPDATE_FIELD_ERROR,
                field: fieldName,
                error: validationError,
              });
              break;
            } else {
              dispatch({
                type: UPDATE_FIELD_ERROR,
                field: fieldName,
                error: "",
              });
            }
          }
        }
      }

      if (Object.keys(errorsInTab).length > 0) {
        errorsByTab[tabId] = errorsInTab;
      }
    });

  updateTabsStatus(state, dispatch, errorsByTab);

  return Object.keys(errorsByTab).length === 0;
};

export const updateTabsStatus = (
  state: any,
  dispatch: Dispatch<any>,
  errors: object
) => {
  const keysToGet = [
    GENERAL,
    REMUNERATION,
    BILLING,
    TIME_TABLE,
    COST_CENTER,
    INTERNAL_INFO,
  ];

  const selectKeys = keysToGet
    .map((key) => {
      if (errors.hasOwnProperty(key)) {
        const tabIndex = findTabObjectById(state, key);
        if (tabIndex !== undefined && tabIndex !== null) {
          dispatch({
            type: UPDATE_TAB_ERROR,
            tabIndex,
            error: true,
          });
        }
        return tabIndex;
      }
      return null;
    })
    .filter((key) => key !== null);

  const tabIndexes = [0, 1, 2, 3, 4, 5];
  tabIndexes.forEach((index) => {
    if (!selectKeys.includes(index)) {
      dispatch({
        type: UPDATE_TAB_ERROR,
        tabIndex: index,
        error: false,
      });
    }
  });
};

const findTabObjectById = (state: any, idToFind: string) => {
  return state.tabDetails.findIndex((tab: any) => tab.id === idToFind);
};

export const validateScheduleTimings = (
  schedule: TimeStates,
  from: Date | null,
  to: Date | null,
  dispatch: Dispatch<any>
): boolean => {
  if (!from || !to) {return false;}

  const scheduleItems = Object.values(schedule);

  // Check if any item has anything filled
  const hasAnyFilledItem = scheduleItems.some(item =>
    item.total && item.total.trim() !== '' && item.total !== '0,00'
  );

  const hasAnyItemGreaterThanOrEqual16 = scheduleItems.some(item => {
    if (!item.total || item.total.trim() === '') {return false;}

    // Normalize: comma → dot
    const formattedValue = item.total.replace(",", ".");
    const parsedValue = parseFloat(formattedValue);

    // Safe fallback
    const numericValue = isNaN(parsedValue) ? 0 : parsedValue;

    return numericValue >= 16.0; // block 16 and above
  });

  if (!hasAnyFilledItem) {
    // Nothing filled — show error
    dispatch({
      type: UPDATE_TAB_ERROR,
      tabIndex: 3,
      error: true,
    });
    dispatch({
      type: UPDATE_FIELD_ERROR,
      field: "dateError",
      error: t("Please enter the timings"),
    });

    return false;
  } else if(hasAnyItemGreaterThanOrEqual16){
     dispatch({
      type: UPDATE_TAB_ERROR,
      tabIndex: 3,
      error: true,
    });
     dispatch({
      type: UPDATE_FIELD_ERROR,
      field: "dateError",
      error: t("Working hours must be less than 16 hours per day"),
    });

    return false;
  }

  // At least one item filled — treat as valid
  return true;
};

